"use client";

import { getUserType } from "@nimble/datamodel/enums/userTypes";
import posthog, { SurveyEventName, SurveyEventProperties } from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";

import { SuspendedPostHogPageView } from "@/components/analytics/posthog-page-view";
import { publicEnv } from "@/env/public-env";
import { useDiscoverInfo } from "@/hooks/use-discover-info";
import {
  clearHiringOutcomeSurveySeen,
  HIRING_OUTCOME_COMPLETED_AT_PROPERTY,
  HIRING_OUTCOME_SURVEY_ID,
  isHiringOutcomeYesResponse,
  markHiringOutcomeSurveySeen,
} from "@/lib/posthog/surveys";
import type { User } from "@/lib/types/auth";
import {
  getUserProfileStep,
  ProfileStep,
} from "@/lib/types/user-discover-info";
import { useAuth } from "@/stores/use-auth";

function PostHogUserIdentifier() {
  const { isLoading, user } = useAuth();
  const posthogClient = usePostHog();
  const discoverInfoQuery = useDiscoverInfo();
  const lastIdentifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || !posthogClient) return;
    if (user && discoverInfoQuery.isLoading) return;

    if (user) {
      if (lastIdentifiedUserId.current !== user.id) {
        clearHiringOutcomeSurveySeen();
        identifyUser(
          posthogClient,
          user,
          getUserProfileStep(discoverInfoQuery.data),
        );
        lastIdentifiedUserId.current = user.id;
      }
    } else if (lastIdentifiedUserId.current !== null) {
      clearHiringOutcomeSurveySeen();
      posthogClient.reset();
      posthogClient.register({ app_name: "discover" });
      lastIdentifiedUserId.current = null;
    }
  }, [
    isLoading,
    user,
    posthogClient,
    discoverInfoQuery.isLoading,
    discoverInfoQuery.data,
  ]);

  return null;
}

function identifyUser(
  posthogClient: ReturnType<typeof usePostHog>,
  user: User,
  profileStep: ProfileStep,
) {
  const userTypeLabel =
    getUserType(user.profile?.user_type)?.label ?? "Unknown";

  posthogClient.identify(user.id, {
    email: user.email,
    name: user.name,
    signed_up_at: user.date_joined,
    user_type: userTypeLabel,
    job_seeking_status: user.preference?.job_seeking_status ?? null,
    profile_complete: profileStep === ProfileStep.COMPLETE,
  });
}

export const PosthogProvider = (props: { children: React.ReactNode }) => {
  useEffect(() => {
    if (publicEnv.NEXT_PUBLIC_POSTHOG_KEY && !posthog.__loaded) {
      posthog.init(publicEnv.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: publicEnv.NEXT_PUBLIC_POSTHOG_HOST,
        person_profiles: "identified_only",
        capture_pageview: false,
        capture_pageleave: true,
        debug:
          (publicEnv.NEXT_PUBLIC_SITE_URL.includes("localhost") ||
            publicEnv.NEXT_PUBLIC_SITE_URL.includes("ngrok")) &&
          !publicEnv.NEXT_PUBLIC_SITE_URL.includes("hirenimble.com"), //enable debug logging for local testing
        before_send: (event) => {
          if (
            event?.event === SurveyEventName.SHOWN &&
            event.properties?.[SurveyEventProperties.SURVEY_ID] ===
              HIRING_OUTCOME_SURVEY_ID
          ) {
            markHiringOutcomeSurveySeen();
          }
          if (
            event?.event === SurveyEventName.SENT &&
            event.properties?.[SurveyEventProperties.SURVEY_ID] ===
              HIRING_OUTCOME_SURVEY_ID &&
            isHiringOutcomeYesResponse(event.properties)
          ) {
            posthog.setPersonProperties({
              [HIRING_OUTCOME_COMPLETED_AT_PROPERTY]: new Date().toISOString(),
            });
          }
          return event;
        },
        loaded: (posthog) => {
          posthog.register({ app_name: "discover" });
        },
      });
    }
  }, []);

  if (!publicEnv.NEXT_PUBLIC_POSTHOG_KEY) {
    return props.children;
  }

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      {publicEnv.NEXT_PUBLIC_POSTHOG_KEY && <PostHogUserIdentifier />}
      {props.children}
    </PHProvider>
  );
};
